import { createContext, useContext, useEffect, useMemo } from 'react';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';

type Theme = 'dark' | 'light';

interface LearningState {
  completed: number[];
  setCompleted: Dispatch<SetStateAction<number[]>>;
  toggleChapter: (id: number) => void;
  theme: Theme;
  setTheme: Dispatch<SetStateAction<Theme>>;
}

const LearningContext = createContext<LearningState | null>(null);

export function LearningProvider({ children }: { children: ReactNode }) {
  const [completed, setCompleted] = useLocalStorage<number[]>('stm32-learning-progress-v1', []);
  const [theme, setTheme] = useLocalStorage<Theme>('stm32-theme-v1', 'dark');

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  const value = useMemo<LearningState>(() => ({
    completed,
    setCompleted,
    toggleChapter: (id: number) => {
      setCompleted((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
    },
    theme,
    setTheme,
  }), [completed, setCompleted, theme, setTheme]);

  return <LearningContext.Provider value={value}>{children}</LearningContext.Provider>;
}

export function useLearning() {
  const state = useContext(LearningContext);
  if (!state) throw new Error('useLearning must be used within LearningProvider');
  return state;
}
